import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Pickaxe,
  Truck,
  HardHat,
  Fuel,
  Wrench,
  ShieldCheck,
  QrCode,
  ClipboardCheck,
  AlertTriangle,
  FileBadge,
  LineChart,
  Smartphone,
  ArrowRight,
  Check,
  ChevronRight,
  Boxes,
  Sparkles,
  Pentagon,
  Bell,
  Radar,
} from 'lucide-react';
import { trackEvent } from '../lib/analytics';
import ProductTour from '../components/ProductTour';

const INDUSTRIES = [
  {
    icon: Pickaxe,
    name: 'Mining',
    tagline: 'Heavy haulage, draglines & shovels',
    detail:
      'AS 2550 lift planning, pre-start checklists and certificate expiry alerts — fully audit-trailed for site safety officers.',
  },
  {
    icon: Boxes,
    name: 'Equipment Rental',
    tagline: 'Cranes, lifts, generators on-hire',
    detail:
      'Public asset QR codes let hire customers report defects instantly. Component register tracks slings, shackles, chains across every job.',
  },
  {
    icon: Truck,
    name: 'Fleet',
    tagline: 'Trucks, prime movers, light vehicles',
    detail:
      'GPS telemetry auto-syncs hours and odometer. Service intervals trigger work orders automatically — no spreadsheets.',
  },
  {
    icon: HardHat,
    name: 'Construction',
    tagline: 'Site plant, scissor lifts, EWP',
    detail:
      'Inspections + defects + photos captured in the field on mobile. Certificates of compliance always one tap away.',
  },
  {
    icon: Fuel,
    name: 'Oil & Gas',
    tagline: 'Wellsite, downstream, marine support',
    detail:
      'Multi-tenant isolation by design — separate operating companies on one platform, zero data crossover. AS 4991 lifting compliance built-in.',
  },
];

const CAPABILITIES = [
  {
    icon: Boxes,
    title: 'Asset & Component Register',
    body:
      'Track main assets and every auxiliary (slings, shackles, hooks) with SWL ratings, serial numbers and parent linkage. Transfer components between assets with full history.',
    plan: 'free',
    planNote: 'Up to 10 assets free',
  },
  {
    icon: ClipboardCheck,
    title: 'Inspections & Defects',
    body:
      'Pre-start checklists, photo capture, GPS-tagged inspections. Failed items auto-create defects. Severity-graded, signed-off, audit-ready.',
    plan: 'free',
    planNote: '10 inspections / mo free',
  },
  {
    icon: FileBadge,
    title: 'Compliance & Certificates',
    body:
      'AS 2550, AS 1418, AS 4991, AS 2359 seeded. Certificates auto-flag at 30 days expiring, email + in-app alerts. Per-tenant standards library.',
    plan: 'free',
    planNote: 'Up to 5 certificates free',
  },
  {
    icon: Smartphone,
    title: 'Mobile Workshop',
    body:
      'Full mobile app for techs — clock on/off, complete tasks, capture signatures, snap defect photos. 4-digit PIN for fast re-access on shared devices.',
    plan: 'pro',
  },
  {
    icon: QrCode,
    title: 'Public Asset QR',
    body:
      'Stick a QR on any asset. Operators and hire customers see status + report defects with zero login. Per-asset opaque tokens — no internal IDs exposed.',
    plan: 'pro',
  },
  {
    icon: LineChart,
    title: 'Cost & Insights',
    body:
      'Labour (per-user hourly rate) + parts + misc tracked per work order. Asset health score, cost per operating hour, on-time completion %.',
    plan: 'free',
    planNote: '30-day history on free · full history + export on Pro',
  },
  {
    icon: Pentagon,
    title: 'Geofences & Fleet Map',
    body:
      'Draw polygons around yards and jobsites — every GPS packet is checked, and you get instant alerts the moment a unit strays off-site. The same status appears as a green tick on the customer-facing QR page.',
    plan: 'pro',
    isNew: true,
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Sign up free',
    body: 'No card, no sales call. 30 seconds — name, email, company. We auto-seed a demo fleet so you can explore everything instantly.',
  },
  {
    n: '02',
    title: 'Import your fleet',
    body: 'CSV upload with column mapping and duplicate detection — 500 assets in under a minute. Or add them one by one.',
  },
  {
    n: '03',
    title: 'Inspect in the field',
    body: 'Crews open the mobile app, scan a QR, run the checklist. Defects, photos and signatures flow into your workshop queue.',
  },
];

const DIFFERENTIATORS = [
  'Multi-tenant isolation audited end-to-end — zero cross-tenant leakage',
  'Australian Standards seeded out-of-the-box (AS 2550 / 1418 / 4991 / 2359)',
  'Geofences with sub-second enter / exit alerts on every GPS packet',
  'Public asset QR for hire customers — no login, no friction',
  'Telemetry-agnostic ingest — bring any GPS provider',
  'Certificate expiry alerts (in-app + email) with 30-day warning window',
  'Mobile-first workshop mode with PIN re-auth for shared devices',
];

const FAQS = [
  {
    q: 'How long does it take to get started?',
    a: 'Two minutes to your first asset. Test-drive signup ships a pre-seeded demo fleet so you can poke around before adding your own data. A typical onboarding (your assets + first inspection template + first user invite) takes under an hour.',
  },
  {
    q: 'Who owns the data?',
    a: 'You do. Guardian is multi-tenant with strict isolation — your data is never visible to other tenants and we never look at it for AI training or analytics. Export everything to CSV anytime. Cancel and your data is yours to keep for 90 days before deletion.',
  },
  {
    q: 'Do you integrate with existing systems?',
    a: 'Yes — Guardian ships with the EQUIPR Allocate integration for hire-tracking sync, a webhook ingestion endpoint for any GPS provider, and a Teltonika-compatible TCP Gateway for direct hardware. Custom integrations are available on Enterprise.',
  },
  {
    q: 'What about offline / poor-signal sites?',
    a: 'Mobile workshop mode caches the day\'s inspections + work orders locally and syncs the moment connectivity returns. Inspections submitted offline never get lost.',
  },
  {
    q: 'How do I cancel?',
    a: 'Settings → Billing → Manage subscription opens the Stripe Customer Portal where you can cancel in one click. Pro features stay active until the end of the current billing period — no proration penalty, no awkward sales calls.',
  },
  {
    q: 'Is my data secure?',
    a: 'JWT-based auth with bcrypt password hashing, HTTPS-only, MongoDB Atlas with at-rest encryption, role-based access (Admin / Fleet Manager / Mechanic), and a complete audit trail of plan changes, IMEI binds, and user-management events. Hosted in Australian data centres.',
  },
];

export default function LandingPage() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated tenant users straight to their dashboard
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleCtaClick = (location) => {
    trackEvent('landing_cta_clicked', { location });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-foreground font-body landing-page" data-testid="landing-page">
      {/* Page-wide ambient backdrop (CSS only, no images) */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage: `
            radial-gradient(circle at 12% 8%, hsla(48, 100%, 50%, 0.08) 0%, transparent 38%),
            radial-gradient(circle at 88% 6%, hsla(220, 60%, 28%, 0.15) 0%, transparent 45%),
            radial-gradient(circle at 50% 100%, hsla(0, 0%, 13%, 1) 0%, transparent 70%),
            linear-gradient(135deg, #0a0a0a 0%, #131313 100%)
          `,
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.018) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.018) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 90% 80% at 50% 30%, black 30%, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 80% at 50% 30%, black 30%, transparent 85%)',
        }}
      />

      {/* ── Top Nav ── */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-sm bg-[#FFD108] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">
              EQUIPR <span className="text-[#FFD108]">GUARDIAN</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#industries" className="hover:text-white transition-colors" data-testid="nav-industries">Industries</a>
            <a href="#geofences" className="hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-geofences">
              Geofences
              <span className="text-[8px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#FFD108] text-black">New</span>
            </a>
            <a href="#tour" className="hover:text-white transition-colors" data-testid="nav-tour">Tour</a>
            <a href="#capabilities" className="hover:text-white transition-colors" data-testid="nav-capabilities">Platform</a>
            <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how-it-works">How it works</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-flex h-9 items-center px-3 text-sm text-muted-foreground hover:text-white transition-colors"
              data-testid="nav-login"
            >
              Sign in
            </Link>
            <Link
              to="/test-drive"
              onClick={() => handleCtaClick('nav')}
              className="btn-volt inline-flex items-center gap-2 h-9 px-4 text-sm rounded-sm"
              data-testid="nav-test-drive"
            >
              Start free
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-xs uppercase tracking-widest text-muted-foreground mb-8" data-testid="hero-eyebrow">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFD108] animate-pulse" />
                Built for heavy industry
              </div>

              <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="hero-headline">
                Asset safety, inspections & compliance.
                <br />
                <span className="text-[#FFD108]">One platform.</span> Built for the field.
              </h1>

              <p className="mt-7 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-subhead">
                Guardian puts you in command of your entire fleet. Track assets and components,
                run inspections from the field, get ahead of certification expiries, and maintain
                an unbreakable audit trail — all from one platform built specifically for
                crane hire, operated plant, mining, construction, and oil &amp; gas operations.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/test-drive"
                  onClick={() => handleCtaClick('hero_primary')}
                  className="btn-volt inline-flex items-center gap-2 h-12 px-6 text-sm rounded-sm"
                  data-testid="hero-primary-cta"
                >
                  Start free test drive
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  onClick={() => handleCtaClick('hero_secondary')}
                  className="inline-flex items-center gap-2 h-12 px-5 text-sm rounded-sm border border-white/15 hover:border-[#FFD108]/50 hover:bg-white/[0.03] transition-colors"
                  data-testid="hero-secondary-cta"
                >
                  Sign in
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-widest text-muted-foreground">
                <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]" /> Free up to 10 assets</span>
                <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]" /> No credit card</span>
                <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]" /> Demo fleet pre-seeded</span>
              </div>
            </div>

            {/* Hero visual — control-panel style stat board, no images */}
            <div className="lg:col-span-5">
              <HeroStatBoard />
            </div>
          </div>
        </div>
      </section>

      {/* ── Product Tour (auto-advancing screenshot carousel) ── */}
      <ProductTour />

      {/* ── Geofences spotlight — the killer-feature panel ── */}
      <GeofenceSpotlight onCtaClick={handleCtaClick} />

      {/* ── Industry strip ── */}
      <section id="industries" className="relative z-10 border-y border-white/5 bg-black/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="label-overline mb-3" data-testid="industries-overline">Five verticals · one stack</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="industries-headline">
                Built for the industries that move <span className="text-[#FFD108]">heavy iron</span>.
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md text-sm">
              From a 4-asset crane hire to a 4,000-unit national fleet — the same primitives scale across every vertical we serve.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.icon;
              return (
                <div
                  key={ind.name}
                  className="group relative p-5 rounded-sm border border-white/8 bg-white/[0.02] hover:border-[#FFD108]/40 hover:bg-white/[0.04] transition-all"
                  data-testid={`industry-card-${ind.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm bg-[#FFD108]/10 border border-[#FFD108]/20 grid place-items-center group-hover:bg-[#FFD108]/15 transition-colors">
                      <Icon className="w-5 h-5 text-[#FFD108]" strokeWidth={2} />
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-1">{ind.name}</h3>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{ind.tagline}</p>
                  <p className="text-sm text-muted-foreground/90 leading-relaxed">{ind.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Capabilities ── */}
      <section id="capabilities" className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-3xl mb-16">
            <div className="label-overline mb-3" data-testid="capabilities-overline">The platform</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="capabilities-headline">
              Every workflow your safety officer, fleet manager and
              <span className="text-[#FFD108]"> workshop foreman </span>
              already runs — connected.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-sm overflow-hidden">
            {CAPABILITIES.map((cap) => {
              const Icon = cap.icon;
              const isPro = cap.plan === 'pro';
              return (
                <div
                  key={cap.title}
                  className="relative p-7 bg-[#0d0d0d] hover:bg-[#121212] transition-colors group"
                  data-testid={`capability-${cap.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {/* Top-right badge stack: NEW (when applicable) sits above plan tag */}
                  <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
                    {cap.isNew && (
                      <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-[#FFD108] text-black">
                        New
                      </span>
                    )}
                    <span
                      className={`text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                        isPro
                          ? 'border-[#FFD108]/40 text-[#FFD108] bg-[#FFD108]/5'
                          : 'border-white/15 text-muted-foreground bg-white/[0.02]'
                      }`}
                      data-testid={`capability-plan-${cap.plan}`}
                      title={isPro ? 'Pro plan' : 'Available on the Free plan'}
                    >
                      {isPro ? 'Pro' : 'Free'}
                    </span>
                  </div>

                  <Icon className="w-7 h-7 text-[#FFD108] mb-5" strokeWidth={1.75} />
                  <h3 className="font-heading font-bold text-xl mb-3">{cap.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cap.body}</p>
                  {cap.planNote && (
                    <p className="mt-4 text-[11px] uppercase tracking-widest text-muted-foreground/70 font-mono">
                      {cap.planNote}
                    </p>
                  )}
                  <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[#FFD108]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how" className="relative z-10 border-t border-white/5 bg-black/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-4">
              <div className="label-overline mb-3" data-testid="how-overline">How it works</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="how-headline">
                Live on Guardian in
                <span className="text-[#FFD108]"> under an hour</span>.
              </h2>
              <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
                We pre-seed a working demo so you can explore every feature before you put a single real asset in.
                When you're ready, bulk-import your fleet and invite your team.
              </p>
              <Link
                to="/test-drive"
                onClick={() => handleCtaClick('how_it_works')}
                className="mt-8 inline-flex items-center gap-2 text-sm text-[#FFD108] hover:gap-3 transition-all"
                data-testid="how-cta"
              >
                Start now <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-8 space-y-4">
              {STEPS.map((step, idx) => (
                <div
                  key={step.n}
                  className="grid grid-cols-[auto_1fr] gap-6 lg:gap-8 p-6 lg:p-7 rounded-sm border border-white/8 bg-white/[0.02] hover:border-[#FFD108]/30 transition-colors"
                  data-testid={`step-${idx + 1}`}
                >
                  <div className="font-mono text-3xl lg:text-4xl text-[#FFD108]/70 leading-none">{step.n}</div>
                  <div>
                    <h3 className="font-heading font-bold text-xl mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Differentiators ── */}
      <section className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="label-overline mb-3" data-testid="why-overline">Why Guardian</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="why-headline">
                Engineered for sites where
                <span className="text-[#FFD108]"> compliance failure has consequences</span>.
              </h2>
              <div className="mt-8 p-5 rounded-sm border border-[#FFD108]/20 bg-[#FFD108]/[0.04]">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="w-4 h-4 text-[#FFD108]" />
                  <span className="text-xs uppercase tracking-widest text-[#FFD108]/90 font-medium">Audit-ready by default</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every inspection, defect, certificate change and work-order completion is timestamped, user-attributed and immutable.
                  Export the trail when the regulator calls.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-3">
                {DIFFERENTIATORS.map((d, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-sm border border-white/8 bg-white/[0.02]"
                    data-testid={`differentiator-${i}`}
                  >
                    <div className="mt-0.5 w-5 h-5 rounded-sm bg-[#FFD108]/10 border border-[#FFD108]/30 grid place-items-center flex-shrink-0">
                      <Check className="w-3 h-3 text-[#FFD108]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-foreground/90 leading-snug">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing teaser ── */}
      <section id="pricing" className="relative z-10 border-t border-white/5 bg-black/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="label-overline mb-3" data-testid="pricing-overline">Pricing</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="pricing-headline">
              Start free. <span className="text-[#FFD108]">Scale when you're ready.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="p-8 rounded-sm border border-white/8 bg-white/[0.02]" data-testid="plan-free">
              <div className="label-overline mb-3">Free</div>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-heading font-extrabold text-5xl">$0</span>
                <span className="text-sm text-muted-foreground">/ month</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {['Up to 10 assets (hard cap)', 'Up to 5 office users · unlimited mechanics', 'Unlimited inspections', 'Mobile workshop mode', 'Public QR for every asset', 'Demo fleet pre-seeded'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-[#FFD108] flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/test-drive"
                onClick={() => handleCtaClick('pricing_free')}
                className="block w-full text-center h-11 leading-[44px] rounded-sm border border-white/15 hover:border-[#FFD108]/50 hover:bg-white/[0.04] text-sm transition-colors"
                data-testid="pricing-free-cta"
              >
                Start free
              </Link>
            </div>

            <div className="relative p-8 rounded-sm border border-[#FFD108]/40 bg-gradient-to-br from-[#FFD108]/[0.06] to-transparent" data-testid="plan-pro">
              <div className="absolute -top-3 left-8 px-2 py-0.5 bg-[#FFD108] text-black text-[10px] uppercase tracking-widest font-bold rounded-sm">
                Recommended
              </div>
              <div className="label-overline mb-3 text-[#FFD108]/90">Professional</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-heading font-extrabold text-5xl">$39</span>
                <span className="text-sm text-muted-foreground">per office user / month</span>
              </div>
              <div className="mb-6 text-xs uppercase tracking-widest text-[#FFD108]/90 font-medium flex items-center gap-2">
                <Check className="w-3.5 h-3.5" />
                Field users (operators &amp; mechanics) — free, unlimited
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {[
                  'Unlimited assets',
                  'Guardian AI — chat, work-order assistant, insights',
                  'Bulk PM scheduling across asset categories',
                  'Task Library auto-seeded into work orders',
                  'Compliance standards library',
                  'Cost tracking + insights dashboard',
                  'Certificate expiry alerts',
                  'Priority email support',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-foreground/90">
                    <Check className="w-4 h-4 text-[#FFD108] flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* GPS add-on call-out */}
              <div className="mb-8 p-3 rounded-sm border border-[#FFD108]/20 bg-black/30" data-testid="pricing-gps-addon">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs uppercase tracking-widest text-[#FFD108]/90 font-bold">GPS Telemetry add-on</span>
                  <span className="font-mono text-sm font-semibold text-foreground/90">$1.99 / asset / mo</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Pay only for the assets you GPS-enable. Prorated automatically.
                </p>
              </div>
              <Link
                to="/test-drive"
                onClick={() => handleCtaClick('pricing_pro')}
                className="btn-volt block w-full text-center h-11 leading-[44px] rounded-sm text-sm"
                data-testid="pricing-pro-cta"
              >
                Start free, upgrade later
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="relative z-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-overline mb-3" data-testid="faq-overline">FAQ</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="faq-headline">
              Questions buyers <span className="text-[#FFD108]">ask first.</span>
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((item, i) => (
              <details
                key={i}
                className="group rounded-sm border border-white/8 bg-white/[0.02] open:bg-white/[0.04] open:border-[#FFD108]/30 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <summary className="cursor-pointer list-none px-5 py-4 flex items-start justify-between gap-4">
                  <span className="text-sm font-medium pr-4">{item.q}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-5 pb-5 -mt-1 text-sm text-muted-foreground leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
          <p className="text-xs text-muted-foreground/70 text-center mt-8">
            Still have questions? Email <a href="mailto:support@equipr.software" className="text-[#FFD108]/90 hover:text-[#FFD108]" data-testid="faq-support-email">support@equipr.software</a>
          </p>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD108]/30 bg-[#FFD108]/[0.05] text-xs uppercase tracking-widest text-[#FFD108]/90 mb-6">
            <Sparkles className="w-3 h-3" />
            30 seconds to your first asset
          </div>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.1]" data-testid="final-cta-headline">
            Stop chasing inspections across
            <br />
            <span className="text-[#FFD108]">spreadsheets and whiteboards.</span>
          </h2>
          <p className="mt-6 text-base text-muted-foreground max-w-xl mx-auto">
            Test drive the full Guardian platform with a pre-seeded demo fleet. No card, no sales call, no commitment.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/test-drive"
              onClick={() => handleCtaClick('final_cta')}
              className="btn-volt inline-flex items-center gap-2 h-12 px-7 text-sm rounded-sm"
              data-testid="final-cta-button"
            >
              Start free test drive
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-white transition-colors"
              data-testid="final-cta-signin"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="relative z-10 border-t border-white/5 bg-black/40">
        {/* Trust strip */}
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-muted-foreground" data-testid="trust-strip">
            <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#FFD108]/80" /> Australian-built &amp; hosted</span>
            <span className="text-muted-foreground/30">·</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]/80" /> Multi-tenant isolated</span>
            <span className="text-muted-foreground/30">·</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]/80" /> Encrypted at rest &amp; in transit</span>
            <span className="text-muted-foreground/30">·</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]/80" /> 99.9% target uptime · Telematics Gateway monitored 24/7</span>
            <span className="text-muted-foreground/30">·</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#FFD108]/80" /> Stripe-secured billing</span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-6 h-6 rounded-sm bg-[#FFD108] grid place-items-center">
              <ShieldCheck className="w-3 h-3 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold">EQUIPR GUARDIAN</span>
            <span className="font-mono text-xs text-muted-foreground/70 ml-2">v1.0</span>
          </div>

          <nav className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#faq" className="hover:text-white transition-colors" data-testid="footer-faq">FAQ</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="footer-pricing">Pricing</a>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-sign-in">Sign in</Link>
            <Link to="/test-drive" className="hover:text-[#FFD108] transition-colors" data-testid="footer-test-drive">Test drive</Link>
          </nav>

          <p className="text-xs text-muted-foreground/70 font-mono">
            © {new Date().getFullYear()} EQUIPR Software
          </p>
        </div>
      </footer>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Hero stat board — control-panel inspired data card (CSS only).
 * Mimics a dispatch screen the way mining/fleet ops centres do.
 * ────────────────────────────────────────────────────────────────── */
function HeroStatBoard() {
  return (
    <div
      className="relative rounded-sm border border-white/10 bg-[#0d0d0d] overflow-hidden"
      data-testid="hero-stat-board"
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 h-10 border-b border-white/8 bg-black/40">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FFD108] animate-pulse" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
            FLEET-CTL · LIVE
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
          AS&nbsp;2550&nbsp;· AS&nbsp;1418
        </span>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-px bg-white/5">
        <Stat label="Assets" value="247" sub="↑ 12 this week" />
        <Stat label="Open WO" value="18" sub="3 overdue" tone="warn" />
        <Stat label="Inspections / 24h" value="42" sub="38 pass · 4 defect" />
        <Stat label="Cert health" value="96%" sub="2 expiring soon" tone="ok" />
      </div>

      {/* Asset log strip */}
      <div className="border-t border-white/8 divide-y divide-white/5">
        {[
          { id: 'EX-104', state: 'IN SERVICE', tone: 'ok', meta: 'Pre-start 06:12' },
          { id: 'CR-22', state: 'DEFECT RAISED', tone: 'warn', meta: 'Boom limit · SEV 2' },
          { id: 'PM-77', state: 'WO ACTIVE', tone: 'info', meta: '500hr service' },
        ].map((row) => (
          <div key={row.id} className="flex items-center justify-between px-4 py-3 text-xs">
            <div className="flex items-center gap-3">
              <span className="font-mono text-foreground">{row.id}</span>
              <StateBadge tone={row.tone}>{row.state}</StateBadge>
            </div>
            <span className="font-mono text-muted-foreground">{row.meta}</span>
          </div>
        ))}
      </div>

      {/* Footer bar */}
      <div className="flex items-center justify-between px-4 h-10 border-t border-white/8 bg-black/40">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono flex items-center gap-2">
          <Wrench className="w-3 h-3" /> Workshop · 3 techs on-shift
        </span>
        <span className="text-[10px] font-mono text-[#FFD108]/80">UPTIME 99.97%</span>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const toneColour =
    tone === 'warn' ? 'text-[#FF4F00]' : tone === 'ok' ? 'text-[#FFD108]' : 'text-foreground';
  return (
    <div className="p-4 bg-[#0d0d0d]">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-2">
        {label}
      </div>
      <div className={`font-mono text-2xl font-medium ${toneColour}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function StateBadge({ tone, children }) {
  const classes =
    tone === 'warn'
      ? 'bg-[#FF4F00]/10 text-[#FF7B3D] border-[#FF4F00]/30'
      : tone === 'ok'
      ? 'bg-[#FFD108]/10 text-[#FFD108] border-[#FFD108]/30'
      : 'bg-white/[0.04] text-foreground/80 border-white/15';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono border rounded-sm ${classes}`}
    >
      {children}
    </span>
  );
}

/**
 * Geofences spotlight — a dedicated panel that sells the killer feature
 * without leaving the landing page. Pure CSS + SVG; no images, no map tile
 * requests so it never blocks page-paint or attracts attribution clutter.
 */
function GeofenceSpotlight({ onCtaClick }) {
  return (
    <section id="geofences" className="relative z-10 border-y border-white/5 bg-black/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD108]/30 bg-[#FFD108]/10 text-[10px] uppercase tracking-widest text-[#FFD108] font-bold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD108] animate-pulse" />
              New · Anti-theft GPS
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]" data-testid="geofence-spotlight-headline">
              Know the moment a unit
              <br />
              <span className="text-[#FFD108]">leaves your yard</span>.
            </h2>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed">
              Draw polygons around yards, depots and jobsites once. Every GPS packet from every
              telematics-enabled asset is checked in real time — and the second a unit crosses
              the boundary, your team gets alerted. Lost-equipment recovery isn't a Monday
              morning surprise anymore.
            </p>

            <ul className="mt-8 space-y-4">
              <SpotlightBullet
                icon={Radar}
                title="Real-time enter / exit detection"
                body="Pure-Python ray-casting runs on every telemetry packet. Sub-second from GPS fix to alert — no batch jobs, no waiting."
              />
              <SpotlightBullet
                icon={Bell}
                title="Instant in-app + email alerts"
                body="Admins and fleet managers get a push the moment an asset strays. Mute per-asset for parked stock so the inbox stays signal-rich."
              />
              <SpotlightBullet
                icon={QrCode}
                title="Customer-facing trust signal"
                body="Hire customers scanning the QR sticker see «Inside designated area ✅». Reassures them mid-rental, recovers trust if the unit wanders."
              />
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/test-drive"
                onClick={() => onCtaClick('geofence_spotlight')}
                className="btn-volt inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm"
                data-testid="geofence-spotlight-cta"
              >
                Try it on the demo fleet
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#capabilities"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#FFD108] transition-colors"
                data-testid="geofence-spotlight-secondary"
              >
                See the full platform <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right: animated SVG mock — yard polygon + 3 markers + an exit alert */}
          <div className="lg:col-span-7">
            <GeofenceVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function SpotlightBullet({ icon: Icon, title, body }) {
  return (
    <li className="flex items-start gap-4">
      <div className="mt-0.5 w-9 h-9 rounded-sm bg-[#FFD108]/10 border border-[#FFD108]/30 grid place-items-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#FFD108]" strokeWidth={2} />
      </div>
      <div>
        <p className="font-heading font-bold text-sm text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function GeofenceVisual() {
  return (
    <div className="relative aspect-[4/3] rounded-sm border border-white/8 bg-[#0a0a0a] overflow-hidden shadow-[0_30px_80px_-20px_rgba(255,209,8,0.18)]">
      {/* Faux map grid background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 30% 40%, hsla(48, 100%, 50%, 0.07), transparent 55%), radial-gradient(circle at 70% 70%, hsla(0, 70%, 50%, 0.06), transparent 50%)',
        }}
      />

      {/* SVG content: yard polygon + assets + an exit trail */}
      <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full">
        <defs>
          <pattern id="diag" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#FFD108" strokeOpacity="0.06" strokeWidth="4" />
          </pattern>
        </defs>

        {/* The yard polygon */}
        <polygon
          points="80,80 280,70 310,180 240,230 100,220 70,150"
          fill="url(#diag)"
          stroke="#FFD108"
          strokeWidth="2"
          strokeDasharray="6 4"
        />
        <text x="86" y="98" fontSize="9" fontFamily="ui-monospace, monospace" fill="#FFD108" letterSpacing="2">
          NORTH SITE YARD
        </text>

        {/* Inside markers (green = compliant) */}
        <GeoMarker x={150} y={140} tone="ok" pulse />
        <GeoMarker x={220} y={170} tone="ok" />
        <GeoMarker x={120} y={190} tone="ok" />

        {/* Exiting asset: trail + amber alert marker outside the fence */}
        <path
          d="M 240,180 Q 290,180 330,210 T 360,250"
          fill="none"
          stroke="#FF7B3D"
          strokeWidth="2"
          strokeDasharray="4 4"
          opacity="0.7"
        />
        <GeoMarker x={360} y={250} tone="warn" pulse />
        <text x="208" y="170" fontSize="9" fontFamily="ui-monospace, monospace" fill="#FF7B3D" letterSpacing="1">
          CR-22
        </text>
      </svg>

      {/* Floating alert toast — top-right corner */}
      <div className="absolute top-4 right-4 max-w-[230px] rounded-sm border border-[#FF4F00]/40 bg-[#1a0e08]/95 backdrop-blur-sm px-3 py-2.5 shadow-lg" data-testid="geofence-visual-alert">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-3.5 h-3.5 text-[#FF7B3D]" />
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#FF7B3D]">Geofence exit</span>
        </div>
        <p className="text-xs font-semibold text-foreground">CR-22 left North Site Yard</p>
        <p className="text-[10px] text-muted-foreground font-mono mt-1">−27.4012, 153.0218 · 2s ago</p>
      </div>

      {/* Live indicator bottom-right */}
      <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-black/70 border border-white/10 text-[9px] uppercase tracking-widest text-muted-foreground font-mono">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Live · 247 assets tracked
      </div>
    </div>
  );
}

function GeoMarker({ x, y, tone = 'ok', pulse = false }) {
  const dot = tone === 'warn' ? '#FF7B3D' : '#10b981';
  const ring = tone === 'warn' ? 'rgba(255,123,61,0.25)' : 'rgba(16,185,129,0.25)';
  return (
    <g>
      {pulse && (
        <circle cx={x} cy={y} r="14" fill={ring}>
          <animate attributeName="r" from="6" to="18" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.6" to="0" dur="1.6s" repeatCount="indefinite" />
        </circle>
      )}
      <circle cx={x} cy={y} r="6" fill={dot} stroke="#000" strokeWidth="1.5" />
    </g>
  );
}

